import React, { useState } from "react";
import { Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import ModalHeader from "./ModalHeader";
import { useForm } from "react-hook-form";
import ModalFooter from "./ModalFooter";
import { postFormData } from "../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import "react-image-crop/dist/ReactCrop.css";
import ReactCrop from "react-image-crop";
import { useEffect } from "react";
import { urltoFile, getCroppedImg } from "../utils/cropImageUtils";
import axios from "axios";
import LinearProgress from "@mui/material/LinearProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

export const BASE_URL = "https://api.biggbrains.com/";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  height: "max-content",
  overflowY: "scroll",
  p: 2,
};

const UploadImageModal = ({ endpt, getUserAgain, updateImage }) => {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(false);
  const [selectedFile, setSelectedFile] = useState();
  const [preview, setPreview] = useState();
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    x: 0,
    y: 0,
    width: 50,
    height: 50,
    aspect: 1 / 1,
  });
  const [result, setResult] = useState(null);
  const [fileType, setFileType] = useState();
  const [smallFile, setSmallFile] = useState();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [chunkSize, setChunkSize] = useState(20000); 

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setPreview("");
    setSelectedFile("");
    setUploadProgress(0);
    setIsUploading(false);
  };

  const onSelectFile = (e) => {
    if (!e.target.files) {
      setSelectedFile(undefined);
      return;
    } else {
      setSelectedFile(e.target.files[0]);
      setFileType({
        name: e.target.files[0].name,
        type: e.target.files[0].type,
      });
    }
  };

  // Preview effect
  React.useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  // Crop effect
  useEffect(() => {
    if (selectedFile && preview) {
      getCroppedImg(image, crop, setResult, fileType);
      urltoFile(result, fileType && fileType.name, fileType && fileType.type)
        .then((file) => setSmallFile(file))
        .catch((err) => {
          console.log(err);
        });
    }
  }, [crop]);

  const splitBase64IntoFixedChunks = (base64, chunkCount = 10) => {
  const payload = {};
  const chunkLength = Math.ceil(base64.length / chunkCount);

  let index = 1;
  for (let i = 0; i < base64.length; i += chunkLength) {
    payload[`profile_image${index}`] = base64.slice(
      i,
      i + chunkLength
    );
    index++;
  }

  return payload;
};


  // 20KB–50KB is ideal for Base64 chunks
  const BASE64_CHUNK_SIZE = chunkSize; // reuse your state value

  // Convert file to Base64 (without metadata)
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.replace(/^data:image\/\w+;base64,/, "");
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Split Base64 string into chunks
  const splitBase64IntoChunks = (base64, size) => {
    const payload = {};
    let index = 1;

    for (let i = 0; i < base64.length; i += size) {
      payload[`profile_image${index}`] = base64.slice(i, i + size);
      index++;
    }

    return payload;
  };

  // Main upload handler
  const handleImageUpload = async () => {
    if (!smallFile) {
      apiErrorToast("Please select and crop an image first");
      return;
    }

    setRequest(true);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 1. Convert cropped image to Base64
      const base64 = await fileToBase64(smallFile);

      // 2. Split Base64 into chunks
      const payload = splitBase64IntoFixedChunks(base64, 10);

      // 3. Upload all chunks in ONE request
      const res = await axios.post(
        `${BASE_URL}user/uploadProfileImg`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percent);
          },
        }
      );

      // 4. Success handling
      okSuccessToast(res.data.message || "Image uploaded successfully");
      setOpen(false);
      setPreview("");
      setSelectedFile("");
      setUploadProgress(0);

      if (getUserAgain) getUserAgain();
    } catch (err) {
      console.error(err);
      apiErrorToast(err.response?.data?.message || "Upload failed");
    } finally {
      setRequest(false);
      setIsUploading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
      }}
    >
      <Tooltip title="Upload Image">
        <IconButton aria-label="upload_image" onClick={handleOpen}>
          <AddAPhotoOutlinedIcon sx={{ color: "#000" }} />
        </IconButton>
      </Tooltip>
      <Box>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style} className="sm_modal">
            <ModalHeader title="Upload Image" handleClose={handleClose} />
            <Box
              component="form"
              id="upload-image"
              validate
              autoComplete="off"
              onSubmit={handleSubmit(handleImageUpload)}
              sx={{
                "& .MuiTextField-root": { m: 1 },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  p: "18px",
                  pt: "0px",
                  pb: "2px",
                  mt: "5px",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <div className="upload-field-customized">
                    <input
                      name="signature"
                      type="file"
                      id="formFile"
                      {...register("signature", {
                        required: "Please choose image before uploading",
                        validate: async (value) => {
                          if (value[0]) {
                            const fileTypes = [
                              "jpg",
                              "JPG",
                              "png",
                              "PNG",
                              "JPEG",
                              "jpeg",
                            ];
                            const messageFiles = ["jpg", "png", "jpeg"];
                            const splittedArry = value[0].name.split(".");
                            const actualType =
                              value[0].name.split(".")[splittedArry.length - 1];

                            if (!fileTypes.includes(actualType)) {
                              return `please upload a valid file format ${messageFiles}`;
                            }

                            const fileSize = Math.round(value[0].size / 1024);
                            if (fileSize > 600) {
                              return `File should be less than 600Kb`;
                            }
                          }
                        },
                      })}
                      onChange={onSelectFile}
                      className={`form-control ${
                        selectedFile || preview ? "sign-form1" : "sign-form"
                      }`}
                    />
                    <span
                      style={{
                        height: "100%",
                        minHeight: "150px",
                        position: "relative",
                      }}
                    >
                      {preview ? (
                        <>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              height: "100%",
                            }}
                          >
                            <ReactCrop
                              keepSelection={true}
                              style={{
                                width: "100%",
                                maxWidth: "48%",
                              }}
                              src={preview}
                              onImageLoaded={setImage}
                              crop={crop}
                              onChange={setCrop}
                            />
                            <div
                              style={{
                                width: "100%",
                                maxWidth: "48%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <img src={result} alt="cropped" />
                            </div>
                          </div>
                          <EditOutlinedIcon
                            fontSize="small"
                            className="reupload-icon"
                          />
                        </>
                      ) : (
                        <AddIcon className="icon-add" />
                      )}
                    </span>
                  </div>
                </div>
              </Box>

              {/* Upload Progress Bar */}
              {isUploading && (
                <Box sx={{ width: "100%", p: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography
                    sx={{
                      textAlign: "center",
                      mt: 1,
                      fontSize: "14px",
                      color: "primary.main",
                    }}
                  >
                    Uploading: {uploadProgress}%
                  </Typography>
                </Box>
              )}

              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  color: "red",
                  fontWeight: "bold",
                  marginLeft: "1.2rem",
                }}
              >
                {errors?.signature && errors.signature.message}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  color: "#9092a3",
                  fontWeight: "bold",
                  marginLeft: "1.2rem",
                  marginBottom: "0.4rem",
                  marginTop: "0.4rem",
                }}
              >
                Only (jpg, png, jpeg)
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  color: "#9092a3",
                  fontWeight: "bold",
                  marginLeft: "1.2rem",
                }}
              >
                Maximum Size: 600Kb
              </Typography>

              {/* Chunk Size Selection (optional) */}
              {smallFile && smallFile.size > 100000 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    File size: {(smallFile.size / 1024).toFixed(2)} KB
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Chunk Size</InputLabel>
                    <Select
                      value={chunkSize}
                      label="Chunk Size"
                      onChange={(e) => setChunkSize(e.target.value)}
                    >
                      <MenuItem value={25000}>
                        25 KB (Slower, more reliable)
                      </MenuItem>
                      <MenuItem value={50000}>50 KB (Balanced)</MenuItem>
                      <MenuItem value={100000}>100 KB (Faster)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              <ModalFooter
                form="upload-image"
                request={request || isUploading}
                btn={isUploading ? "Uploading..." : "Upload"}
              />
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};

// const UploadImageModal = () => {
//   const chunkSize = 20000; // 20KB
//   const token = localStorage.getItem("access_token");

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     const reader = new FileReader();
//     reader.onloadend = () => {
//       const base64 = reader.result.replace(/^data:image\/\w+;base64,/, "");

//       const payload = {};
//       let index = 1;

//       for (let i = 0; i < base64.length; i += chunkSize) {
//         payload[`profile_image${index}`] = base64.slice(i, i + chunkSize);
//         index++;
//       }

//       uploadChunks(payload);
//     };

//     reader.readAsDataURL(file);
//   };

//   const uploadChunks = async (payload) => {
//     try {
//       const res = await axios.post(
//         `${BASE_URL}user/uploadProfileImg`,
//         payload,
//         {
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       alert("Upload successful ✅");
//       console.log(res.data);
//     } catch (err) {
//       alert("Upload failed ❌");
//       console.error(err.response?.data || err);
//     }
//   };

//   return (
//     <label style={styles.box}>
//       <input type="file" accept="image/*" hidden onChange={handleFileChange} />
//       <div style={styles.icon}>⬆️</div>
//       <div style={styles.text}>Upload Profile Image</div>
//     </label>
//   );
// };
// const styles = {
//   box: {
//     width: "260px",
//     height: "160px",
//     border: "2px dashed #4f46e5",
//     borderRadius: "12px",
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     justifyContent: "center",
//     cursor: "pointer",
//     background: "#f9fafb",
//   },
//   icon: {
//     fontSize: "32px",
//     marginBottom: "8px",
//   },
//   text: {
//     fontSize: "14px",
//     color: "#374151",
//   },
// };
export default UploadImageModal;
